// Pure path/ignore/conflict rules (DESIGN §1 conflict rules + §14 path
// policy). These are REAL production functions — the Bridge, MergePlanner,
// and Layer-2 tests all call them; they are not stubbed.

/** Max path length in bytes (DESIGN §14). */
export const MAX_PATH_BYTES = 1024;

const encoder = new TextEncoder();

// Control chars U+0000–U+001F and U+007F, forbidden in paths (DESIGN §14).
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[ -]/;

/** Byte length of a UTF-8 path. */
export function pathByteLength(path: string): number {
  return encoder.encode(path).length;
}

/**
 * Ignore rule (DESIGN §14): dotfiles and dot-directories at any depth
 * (includes `.obsidian`, `.trash`, `.git`, `.DS_Store`). Not configurable in
 * v1.
 */
export function isIgnored(path: string): boolean {
  return path.split("/").some((seg) => seg.startsWith("."));
}

/**
 * Uniqueness key for a path (DESIGN §14): compare NFC-normalized and
 * case-insensitive. Path *bytes* are preserved as authored elsewhere; this
 * key is only for collision detection.
 */
export function pathKey(path: string): string {
  return path.normalize("NFC").toLowerCase();
}

/** Whether two paths collide under the uniqueness rule. */
export function pathsCollide(a: string, b: string): boolean {
  return pathKey(a) === pathKey(b);
}

/**
 * Whether two byte-different paths denote the *same* file rather than a
 * case-collision: equal after NFC normalization, case **preserved** (DESIGN
 * §14). An NFD filename from a mac client and the NFC form from a linux client
 * are the same file (dedupe, no conflict); `README.md` vs `readme.md` are not
 * (that collides → conflict rename).
 */
export function sameFile(a: string, b: string): boolean {
  return a.normalize("NFC") === b.normalize("NFC");
}

/**
 * Path validity (DESIGN §14): forbid `/` segments `.` and `..`, control
 * chars, empty paths/segments, and paths over `MAX_PATH_BYTES`.
 */
export function isValidPath(path: string): boolean {
  if (path.length === 0) return false;
  if (pathByteLength(path) > MAX_PATH_BYTES) return false;
  if (CONTROL_CHARS.test(path)) return false;
  const segments = path.split("/");
  for (const seg of segments) {
    if (seg.length === 0) return false; // no empty segment (leading/trailing/double slash)
    if (seg === "." || seg === "..") return false;
  }
  return true;
}

/**
 * Auto-rename for a path conflict (DESIGN §1 rule 1): the losing file becomes
 * `name (conflict <short-id>).ext`, inserting before the extension.
 */
export function conflictName(path: string, shortId: string): string {
  const slash = path.lastIndexOf("/");
  const dir = slash === -1 ? "" : path.slice(0, slash + 1);
  const base = slash === -1 ? path : path.slice(slash + 1);
  const dot = base.lastIndexOf(".");
  // No extension, or a leading-dot name with no other dot: append at the end.
  if (dot <= 0) {
    return `${dir}${base} (conflict ${shortId})`;
  }
  const stem = base.slice(0, dot);
  const ext = base.slice(dot); // includes the leading dot
  return `${dir}${stem} (conflict ${shortId})${ext}`;
}

/** Live-text policy (DESIGN §14): only `.md` files get a content doc. */
export function isTextFile(path: string): boolean {
  return path.toLowerCase().endsWith(".md");
}
